import { useMutation, useQueryClient } from '@tanstack/react-query';
import { startOfWeek, endOfWeek } from 'date-fns';
import { toast } from 'sonner';
import { scheduleKeys } from './queryKeys';
import { useSchedulingMutations, mapShiftRpcError } from '@/hooks/useSchedulingMutations';

export const useCancelShift = (
  companyId: string | undefined,
  currentWeek: Date
) => {
  const queryClient = useQueryClient();
  const weekStartISO = startOfWeek(currentWeek).toISOString();
  const weekEndISO = endOfWeek(currentWeek).toISOString();
  const { cancelShift } = useSchedulingMutations();
  
  return useMutation({
    mutationFn: async (shiftId: string) => {
      await cancelShift(shiftId);
      return shiftId;
    },
    
    onMutate: async (shiftId) => {
      // Cancel outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && key[0] === 'schedule' && key[1] === 'companyShifts';
        }
      });
      
      // FIX: Collect all matching queries and their data for rollback
      const allMatchingQueries = queryClient.getQueriesData({
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && 
                 key[0] === 'schedule' && 
                 key[1] === 'companyShifts' &&
                 key[2] === companyId &&
                 key[3] === weekStartISO &&
                 key[4] === weekEndISO;
        }
      });
      
      // Optimistically remove shift from all matching query keys (with different employee hashes)
      queryClient.setQueriesData(
        {
          predicate: (query) => {
            const key = query.queryKey;
            return Array.isArray(key) && 
                   key[0] === 'schedule' && 
                   key[1] === 'companyShifts' &&
                   key[2] === companyId &&
                   key[3] === weekStartISO &&
                   key[4] === weekEndISO;
          }
        },
        (old: any[]) => (old || []).filter(shift => shift.id !== shiftId)
      );
      
      return { allMatchingQueries, shiftId };
    },
    
    onSuccess: (shiftId) => {
      toast.success('Shift cancelled successfully');
      
      // Invalidate ALL schedule queries to ensure roster planner updates
      queryClient.invalidateQueries({
        queryKey: scheduleKeys.all
      });
      
      // CRITICAL FIX: Invalidate eligibleSwapCandidates cache to ensure cancelled shifts are removed
      queryClient.invalidateQueries({
        queryKey: [...scheduleKeys.all, 'eligibleSwapCandidates']
      });
    },
    
    onError: (error: any, shiftId, context) => {
      // Rollback optimistic update on error
      if (context?.allMatchingQueries) {
        // Restore all the previous query data
        context.allMatchingQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      
      const errorMessage = mapShiftRpcError(error.message);
      toast.error(errorMessage || 'Failed to cancel shift');
    }
    
    // FIX: Removed onSettled to prevent conflicts with optimistic updates
    // The invalidation now only happens on success
  });
};
